/* eslint-disable @typescript-eslint/no-explicit-any */

import { paramCase } from 'change-case'
import { MutableRefObject, useEffect, useState } from 'react'
import { AssetReference } from '@opallabs/cassini'
import { kuiperClassNames } from '@opallabs/kuiper'
import { Module } from '../../helpers/types'
import { MobileStream, DesktopStream } from './displays'
import { baseStyles } from './styles'

export type PollRef = MutableRefObject<HTMLDivElement>
export type PollNumber = 2 | 3 | 4
export type PollNumberFuncs = {
  increment: (position: PollNumber) => void
  decrement: (position: PollNumber) => void
}
export type FocusState = {
  shouldFocus: boolean
  pollToFocus: number
}
export interface Props {
  accountImage: AssetReference | null
  accountName: string
  accountUsername: string
  content: string
  display: string
  index?: number
  isAccountPremium?: boolean
  isThreaded?: boolean
  modules: Module[]
  pollFour: string
  pollOne: string
  pollOptionNumber: PollNumber
  pollThree: string
  pollTwo: string
  postType: string
  readOnly: boolean
  scheduledAt: string
  timezone: string
  updateText: (val: string, destination: string) => void
}

// TODO: Change default to Mobile Single in a later ticket
const displays: { [key: string]: React.FC<any> } = {
  mobileStream: MobileStream,
  desktopStream: DesktopStream,
  default: MobileStream,
}

const matchDisplay = (display: string): React.FC<any> =>
  displays[display] || displays.default

const minPollNumber = 2
const maxPollNumber = 4

const fillPolls = (pollOptions: string[], pollNumber: number) => {
  if (!pollOptions) return Array.from({ length: pollNumber }, () => '')
  if (pollOptions.length === pollNumber) return pollOptions
  if (pollOptions.length > pollNumber) return pollOptions.slice(0, pollNumber)
  return Array.from({ length: pollNumber }, (x, idx) => pollOptions[idx] || '')
}

const defaultFocusState: FocusState = { shouldFocus: false, pollToFocus: 0 }

export const TwitterPoll: React.FC<Props> = ({
  accountImage,
  accountName,
  accountUsername,
  content,
  display,
  index = -1,
  isAccountPremium,
  isThreaded = false,
  modules = [],
  pollFour,
  pollOne,
  pollOptionNumber = 2,
  pollThree,
  pollTwo,
  postType,
  readOnly = false,
  scheduledAt,
  timezone,
  updateText = () => null,
}) => {
  const RenderDisplay = matchDisplay(display)
  const [pollNumber, setPollNumber] = useState(
    +pollOptionNumber || (2 as PollNumber)
  )
  const [polls, setPolls] = useState(
    fillPolls([pollOne, pollTwo, pollThree, pollFour], pollNumber)
  )
  const [focus, setFocus] = useState(defaultFocusState)
  const modulesLength = modules ? modules.length : 0

  useEffect(() => {
    const newPollNumber = +pollOptionNumber || (2 as PollNumber)
    setPollNumber(newPollNumber)
    setPolls(fillPolls([pollOne, pollTwo, pollThree, pollFour], newPollNumber))
  }, [pollOptionNumber, pollOne, pollTwo, pollThree, pollFour])

  const handleSetPolls = (poll: string, position: number) => {
    setPolls(polls.map((elem, index) => (index === position ? poll : elem)))
    updatePolls(poll, position)
  }

  const updatePolls = (poll: string, position: number) => {
    let updatedPoll = ''
    switch (position) {
      case 0:
        updatedPoll = 'pollOne'
        break
      case 1:
        updatedPoll = 'pollTwo'
        break
      case 2:
        updatedPoll = 'pollThree'
        break
      default:
        updatedPoll = 'pollFour'
    }
    updateText(poll, updatedPoll)
  }

  const incrementPollNumber = (position: number) => {
    if (pollNumber < maxPollNumber && position !== 0) {
      const newPollNumber = (pollNumber + 1) as PollNumber
      setPolls([...polls, ''])
      setPollNumber(newPollNumber)
      updateText(newPollNumber.toString(), 'twitter_thread_poll_option_number')
    }
    setFocus({ shouldFocus: true, pollToFocus: position + 1 })
  }

  const decrementPollNumber = (position: number) => {
    if (pollNumber > minPollNumber && position !== 0) {
      const newPollNumber = (pollNumber - 1) as PollNumber
      const newPolls = polls.filter((x, idx) => idx !== position)
      setPolls(newPolls)
      setPollNumber(newPollNumber)
      updateText(newPollNumber.toString(), 'twitter_thread_poll_option_number')
      ;[0, 1, 2, 3].forEach((index) => {
        const updatedPollText = newPolls[index] || ''
        updatePolls(updatedPollText, index)
      })
    }
    setFocus({ shouldFocus: true, pollToFocus: position - 1 })
  }

  const resolveFocus = (): void => setFocus(defaultFocusState)

  return (
    <div
      css={baseStyles(display, postType)}
      className={kuiperClassNames(
        'twitter twitter-poll',
        `twitter-${paramCase(display)}`
      )}
    >
      <RenderDisplay
        content={content}
        focus={focus}
        resolveFocus={resolveFocus}
        accountName={accountName}
        accountUsername={accountUsername}
        accountImage={accountImage}
        isAccountPremium={isAccountPremium}
        isThreaded={isThreaded}
        index={index}
        modulesLength={modulesLength}
        readOnly={readOnly}
        scheduledAt={scheduledAt}
        timezone={timezone}
        postType={postType}
        polls={polls}
        updatePollNumber={{
          increment: incrementPollNumber,
          decrement: decrementPollNumber,
        }}
        updatePolls={handleSetPolls}
        onContentChange={(val: string): void => updateText(val, 'content')}
      />
    </div>
  )
}
